'use client';

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { productApi } from '../../services/productApi';
import { categoryApi } from '../../services/categoryApi';
import { Product, ProductFormData } from '../../types/product';
import { generateSlug, calculateDiscountPercentage, formatPrice, validateProductForm } from '../../utils/productUtils';
import { uploadFileToStorageV2, uploadImageToStorageV2, deleteImageFromStorage } from '@/utils/storage';
import { ImagePreviewModal } from '@/components/ImagePreviewModal';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Upload, X, Plus, Trash2, ChevronDown, GripVertical, Sparkles, Link2, Globe, DownloadCloud, RefreshCw, CheckCircle2, FileCode, Code2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { aiApi } from '../../services/aiApi';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const getFeaturedOrderInputValue = (featuredOrder?: number | null): string => {
  if (featuredOrder === undefined || featuredOrder === null || featuredOrder < 1) {
    return '';
  }

  return String(featuredOrder);
};

interface Category {
  id: string;
  name: string;
  parentId?: string;
  slug?: string;
  status?: string;
}

// Sortable Image Item Component
interface SortableImageItemProps {
  id: string;
  image: string;
  index: number;
  isPreview: boolean;
  onRemove: (index: number) => void;
  onSetPreview: (image: string) => void;
  onOpenPreview: (image: string) => void;
}

function SortableImageItem({ id, image, index, isPreview, onRemove, onSetPreview, onOpenPreview }: SortableImageItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative rounded-lg overflow-hidden border-2 transition ${
        isPreview ? 'border-blue-500 ring-2 ring-blue-300 shadow-lg' : 'border-gray-200 hover:border-gray-400'
      }`}
    >
      {/* Drag Handle */}
      <div
        {...attributes}
        {...listeners}
        className="absolute top-2 left-2 bg-gray-900/80 text-white p-1.5 rounded cursor-move hover:bg-gray-900 transition z-10"
        title="Sürükle"
      >
        <GripVertical size={16} />
      </div>

      {/* Image */}
      <div className="group relative">
        <img
          src={image}
          alt={`Resim ${index + 1}`}
          className="w-full h-40 object-cover"
        />
        <button
          type="button"
          onClick={() => {
            onSetPreview(image);
            onOpenPreview(image);
          }}
          className="absolute inset-0 flex items-center justify-center bg-black/0 text-white transition group-hover:bg-black/30"
          title="Görseli büyüt"
        >
          <span className="rounded-full bg-black/70 px-3 py-1 text-xs font-medium opacity-0 transition group-hover:opacity-100">
            Büyüt
          </span>
        </button>
      </div>
      
      {/* Index Badge */}
      <div className="absolute top-2 left-12 bg-blue-600 text-white text-xs px-2 py-1 rounded font-semibold">
        #{index + 1}
      </div>

      {/* Delete Button */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          if (window.confirm(`${index + 1}. resmi silmek istediğinden emin misin?`)) {
            onRemove(index);
          }
        }}
        className="absolute top-2 right-2 bg-red-500 text-white p-1.5 rounded-full hover:bg-red-600 transition shadow-lg z-10"
        title="Resmi Sil"
      >
        <Trash2 size={16} />
      </button>

      {/* Primary Badge */}
      {isPreview && (
        <div className="absolute bottom-0 inset-x-0 bg-blue-600 text-white text-xs text-center py-1.5 font-semibold">
          Önizleme Resmi
        </div>
      )}
    </div>
  );
}

export default function AddProductPage() {
  const params = useParams();
  const navigate = useNavigate();
  const productId = params.id as string | undefined;
  const isEditMode = !!productId;

  // Kategorileri ana/alt şeklinde organize et
  const organizeCategories = (cats: Category[]) => {
    const mainCategories = cats.filter(c => !c.parentId);
    const subCategories = (parentId: string) => cats.filter(c => c.parentId === parentId);
    
    return { mainCategories, subCategories };
  };

  const [formData, setFormData] = useState<ProductFormData>({
    title: '',
    slug: '',
    description: '',
    categories: [],
    tags: [],
    images: [],
    videoUrl: '',
    videoPosterUrl: '',
    videoAspectRatio: '9:16',
    instagramVideoUrl: '',
    basePrice: 0,
    campaignPrice: 0,
    discountPercentage: 0,
    costPrice: 0,
    sku: '',
    stock: 0,
    trackInventory: true,
    isAvailable: true,
    isShippable: false,
    isDirectSale: false,
    estimatedShippingDays: undefined,
    isSameDayDelivery: true,
    deliveryFee: 0,
    seoTitle: '',
    seoMetaDescription: '',
    status: 'active',
    featured: false,
    featuredOrder: undefined,
    vendorIds: [],
    extras: [],
  });

  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(isEditMode);
  const [submitting, setSubmitting] = useState(false);
  const [aiLoading, setAiLoading] = useState<string | null>(null);
  const [tagInput, setTagInput] = useState('');
  const [imagePreview, setImagePreview] = useState<string>('');
  const [lightboxImage, setLightboxImage] = useState<string>('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | undefined>>({});

  // URL / HTML Scraper State
  const [importMode, setImportMode] = useState<'url' | 'html'>('url');
  const [importUrl, setImportUrl] = useState('');
  const [importHtml, setImportHtml] = useState('');
  const [isImporting, setIsImporting] = useState(false);

  // Başlık, açıklama ve URL'e göre kategorileri otomatik eşle
  const matchCategoriesForProduct = (
    availableCategories: Category[],
    title: string,
    description: string,
    sourceUrl?: string
  ): string[] => {
    if (!availableCategories || availableCategories.length === 0) return [];

    const normalize = (str: string) =>
      str
        .toLowerCase()
        .replace(/ğ/g, 'g')
        .replace(/ü/g, 'u')
        .replace(/ş/g, 's')
        .replace(/ı/g, 'i')
        .replace(/ö/g, 'o')
        .replace(/ç/g, 'c')
        .replace(/[^a-z0-9\s]/g, ' ')
        .trim();

    const titleNorm = normalize(title);
    const descNorm = normalize(description.slice(0, 1500));
    const urlNorm = normalize(sourceUrl || '');
    const matchedIds = new Set<string>();

    for (const cat of availableCategories) {
      const catNameNorm = normalize(cat.name);
      if (!catNameNorm || catNameNorm.length < 2) continue;

      const rootName = catNameNorm.replace(/(ler|lar|leri|lari|cicekleri|cicegi|cicek)$/g, '').trim();

      // 1. Doğrudan başlıkta veya URL'de geçmesi (en güçlü eşleşme)
      if (titleNorm.includes(catNameNorm) || urlNorm.includes(catNameNorm)) {
        matchedIds.add(cat.id);
        if (cat.parentId) matchedIds.add(cat.parentId);
        continue;
      }

      // 2. Kök kelimenin başlıkta geçmesi (örn: "Güller" kök "gul" -> "Beyaz Gül")
      if (rootName.length >= 3 && (titleNorm.includes(rootName) || urlNorm.includes(rootName))) {
        matchedIds.add(cat.id);
        if (cat.parentId) matchedIds.add(cat.parentId);
        continue;
      }

      // 3. Kategori kelimelerinden birinin başlıkta geçmesi (örn: "Vazoda Çiçekler" -> "vazoda")
      const words = catNameNorm.split(/\s+/).filter((w) => w.length > 2);
      if (words.length > 0 && words.some((w) => titleNorm.includes(w.replace(/(ler|lar|da|de|ta|te)$/g, '')))) {
        matchedIds.add(cat.id);
        if (cat.parentId) matchedIds.add(cat.parentId);
        continue;
      }

      // 4. Açıklamada tam veya kök eşleşme
      if (descNorm.includes(catNameNorm) || (rootName.length >= 4 && descNorm.includes(rootName))) {
        matchedIds.add(cat.id);
        if (cat.parentId) matchedIds.add(cat.parentId);
      }
    }

    return Array.from(matchedIds);
  };

  const generateTagsForProduct = (
    title: string,
    description: string,
    matchedCategoryIds: string[] = [],
    existingTags: string[] = []
  ): string[] => {
    const tagsSet = new Set<string>();

    // 1. Mevcut etiketleri koru
    existingTags.forEach((t) => t && tagsSet.add(t.trim()));

    // 2. Eşleşen kategorilerin isimlerini ekle
    const matchedCategoryNames = categories
      .filter((c) => matchedCategoryIds.includes(c.id))
      .map((c) => c.name.trim());
    matchedCategoryNames.forEach((name) => {
      if (name && name.length >= 3) {
        tagsSet.add(name);
      }
    });

    const fullText = `${title} ${description}`.toLowerCase();
    const normalize = (str: string) =>
      str
        .toLowerCase()
        .replace(/ğ/g, 'g')
        .replace(/ü/g, 'u')
        .replace(/ş/g, 's')
        .replace(/ı/g, 'i')
        .replace(/ö/g, 'o')
        .replace(/ç/g, 'c');

    const norm = normalize(fullText);

    // 3. Çiçek & Bitki Türleri
    const flowerKeywords = [
      { key: 'kirmizi gul', label: 'Kırmızı Gül' },
      { key: 'beyaz gul', label: 'Beyaz Gül' },
      { key: 'pembe gul', label: 'Pembe Gül' },
      { key: 'sari gul', label: 'Sarı Gül' },
      { key: 'gul', label: 'Gül' },
      { key: 'beyaz orkide', label: 'Beyaz Orkide' },
      { key: 'mavi orkide', label: 'Mavi Orkide' },
      { key: 'orkide', label: 'Orkide' },
      { key: 'papatya', label: 'Papatya' },
      { key: 'gerbera', label: 'Gerbera' },
      { key: 'antoryum', label: 'Antoryum' },
      { key: 'lilyum', label: 'Lilyum' },
      { key: 'lale', label: 'Lale' },
      { key: 'sukulent', label: 'Sukulent' },
      { key: 'teraryum', label: 'Teraryum' },
      { key: 'bonsai', label: 'Bonsai' },
      { key: 'karanfil', label: 'Karanfil' },
      { key: 'sakayik', label: 'Şakayık' },
      { key: 'lavanta', label: 'Lavanta' },
      { key: 'ortanca', label: 'Ortanca' },
      { key: 'anastasya', label: 'Anastasya' },
      { key: 'krizantem', label: 'Krizantem' },
      { key: 'kasimpati', label: 'Kasımpatı' },
      { key: 'frezya', label: 'Frezya' },
      { key: 'cipso', label: 'Cipsofilya' },
      { key: 'okaliptus', label: 'Okaliptus' },
      { key: 'baris cicegi', label: 'Barış Çiçeği' },
      { key: 'pasa kilici', label: 'Paşa Kılıcı' },
      { key: 'deve tabani', label: 'Deve Tabanı' },
      { key: 'para cicegi', label: 'Para Çiçeği' },
    ];

    for (const item of flowerKeywords) {
      if (norm.includes(item.key)) {
        tagsSet.add(item.label);
      }
    }

    // 4. Tasarım & Sunum Şekli
    const presentationKeywords = [
      { key: 'cam vazo', label: 'Cam Vazo' },
      { key: 'seramik vazo', label: 'Seramik Vazo' },
      { key: 'vazo', label: 'Vazoda Çiçek' },
      { key: 'beton saksi', label: 'Beton Saksı' },
      { key: 'seramik saksi', label: 'Seramik Saksı' },
      { key: 'saksi', label: 'Saksı Çiçeği' },
      { key: 'buket', label: 'Buket' },
      { key: 'kutuda', label: 'Kutuda Çiçek' },
      { key: 'kutu cicek', label: 'Kutuda Çiçek' },
      { key: 'aranjman', label: 'Aranjman' },
      { key: 'sepet', label: 'Sepette Çiçek' },
      { key: 'tasarim', label: 'Tasarım Çiçek' },
      { key: 'celenk', label: 'Çelenk' },
    ];

    for (const item of presentationKeywords) {
      if (norm.includes(item.key)) {
        tagsSet.add(item.label);
      }
    }

    // 5. Hediyeler, Lezzet & Ekstralar
    const giftKeywords = [
      { key: 'cikolata', label: 'Çikolata' },
      { key: 'gurme', label: 'Gurme Lezzet' },
      { key: 'pelus', label: 'Peluş Ayıcık' },
      { key: 'hediye seti', label: 'Hediye Seti' },
      { key: 'hediye kutusu', label: 'Hediye Kutusu' },
      { key: 'balon', label: 'Uçan Balon' },
      { key: 'pasta', label: 'Butik Pasta' },
    ];

    for (const item of giftKeywords) {
      if (norm.includes(item.key)) {
        tagsSet.add(item.label);
      }
    }

    // 6. Özel Günler & Vesileler
    const occasionKeywords = [
      { key: 'dogum gunu', label: 'Doğum Günü' },
      { key: 'sevgili', label: 'Sevgiliye Çiçek' },
      { key: 'yildonumu', label: 'Yıldönümü' },
      { key: 'yeni is', label: 'Yeni İş Tebriği' },
      { key: 'tebrik', label: 'Tebrik' },
      { key: 'gecmis olsun', label: 'Geçmiş Olsun' },
      { key: 'anneler gunu', label: 'Anneler Günü' },
      { key: 'ozur', label: 'Özür Çiçeği' },
      { key: 'sevgililer gunu', label: 'Sevgililer Günü' },
      { key: 'ogretmenler gunu', label: 'Öğretmenler Günü' },
      { key: 'kadinlar gunu', label: 'Kadınlar Günü' },
      { key: 'taziye', label: 'Taziye' },
      { key: 'dugun', label: 'Düğün & Nikah' },
    ];

    for (const item of occasionKeywords) {
      if (norm.includes(item.key)) {
        tagsSet.add(item.label);
      }
    }

    // 7. Renkler
    const colorKeywords = [
      { key: 'kirmizi', label: 'Kırmızı' },
      { key: 'beyaz', label: 'Beyaz' },
      { key: 'pembe', label: 'Pembe' },
      { key: 'sari', label: 'Sarı' },
      { key: 'mor', label: 'Mor' },
      { key: 'mavi', label: 'Mavi' },
      { key: 'turuncu', label: 'Turuncu' },
      { key: 'lila', label: 'Lila' },
      { key: 'fusya', label: 'Fuşya' },
      { key: 'bordo', label: 'Bordo' },
    ];

    for (const item of colorKeywords) {
      if (norm.includes(item.key)) {
        tagsSet.add(item.label);
      }
    }

    return Array.from(tagsSet).filter((t) => t && t.length >= 2).slice(0, 15);
  };

  const handleAutoGenerateTags = () => {
    if (!formData.title.trim() && !formData.description.trim()) {
      toast.error('Önce ürün başlığı veya açıklaması olmalıdır');
      return;
    }

    const matchedTags = generateTagsForProduct(
      formData.title,
      formData.description,
      formData.categories,
      formData.tags
    );

    if (matchedTags.length > 0) {
      setFormData((prev) => ({
        ...prev,
        tags: matchedTags,
      }));
      toast.success(`${matchedTags.length} etiket otomatik oluşturuldu!`, { icon: '🏷️' });
    } else {
      toast.error('Etiket üretilemedi. Lütfen manuel ekleyin.');
    }
  };

  const handleAutoMatchCategories = () => {
    if (!formData.title.trim() && !formData.description.trim()) {
      toast.error('Önce ürün başlığı veya açıklaması olmalıdır');
      return;
    }

    const matched = matchCategoriesForProduct(categories, formData.title, formData.description);
    if (matched.length > 0) {
      setFormData((prev) => ({
        ...prev,
        categories: Array.from(new Set([...prev.categories, ...matched])),
      }));
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next.categories;
        return next;
      });
      const names = categories.filter((c) => matched.includes(c.id)).map((c) => c.name);
      toast.success(`${names.length} kategori otomatik seçildi: ${names.join(', ')}`, { icon: '🏷️' });
    } else {
      toast.error('Uygun kategori eşleşmesi bulunamadı. Lütfen manuel seçin.');
    }
  };

  const handleImportFromUrl = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!importUrl.trim()) {
      toast.error('Lütfen bir ürün bağlantısı (URL) girin');
      return;
    }

    if (!/^https?:\/\//i.test(importUrl.trim())) {
      toast.error('Lütfen http:// veya https:// ile başlayan geçerli bir bağlantı girin');
      return;
    }

    if (importMode === 'html' && !importHtml.trim()) {
      toast.error('Lütfen kopyaladığınız sayfa kaynağını (HTML) yapıştırın');
      return;
    }

    setIsImporting(true);
    try {
      const scraped = await productApi.importProductFromUrl(
        importUrl.trim(),
        true,
        importMode === 'html' ? importHtml.trim() : undefined
      );

      const data = (scraped as any)?.product || scraped;
      console.log('[Add.tsx] Scraped data to apply:', data);

      if (!data) {
        throw new Error('Ürün verisi alınamadı');
      }

      const parsedTitle = data.title && data.title !== 'İsimsiz Ürün' ? String(data.title).trim() : '';
      const parsedDescription = data.description ? String(data.description).trim() : '';
      const parsedBasePrice = typeof data.basePrice === 'number' ? data.basePrice : (parseFloat(String(data.basePrice || 0)) || 0);
      const parsedCampaignPrice = data.campaignPrice !== undefined && data.campaignPrice !== null
        ? (typeof data.campaignPrice === 'number' ? data.campaignPrice : (parseFloat(String(data.campaignPrice)) || undefined))
        : undefined;

      const validImages = Array.isArray(data.images)
        ? data.images.filter((img: any) => typeof img === 'string' && img.startsWith('http') && !img.includes('data:image') && !img.includes('base64') && !img.includes('R0lGODlh'))
        : [];

      const discount = calculateDiscountPercentage(parsedBasePrice, parsedCampaignPrice || 0);

      const matchedCategoryIds = matchCategoriesForProduct(
        categories,
        parsedTitle,
        parsedDescription,
        importUrl.trim()
      );

      const autoTags = generateTagsForProduct(
        parsedTitle,
        parsedDescription,
        matchedCategoryIds,
        Array.isArray(data.tags) ? data.tags : []
      );

      setFormData((prev) => ({
        ...prev,
        title: parsedTitle || prev.title,
        slug: parsedTitle ? generateSlug(parsedTitle) : prev.slug,
        description: parsedDescription || prev.description,
        basePrice: parsedBasePrice > 0 ? parsedBasePrice : prev.basePrice,
        campaignPrice: parsedCampaignPrice !== undefined ? parsedCampaignPrice : prev.campaignPrice,
        discountPercentage: discount > 0 ? discount : prev.discountPercentage,
        images: validImages.length > 0 ? validImages : prev.images,
        categories: matchedCategoryIds.length > 0 ? matchedCategoryIds : prev.categories,
        tags: autoTags.length > 0 ? autoTags : prev.tags,
        seoTitle: (parsedTitle || prev.seoTitle).slice(0, 60),
        seoMetaDescription: parsedDescription
          ? parsedDescription.replace(/<[^>]*>?/gm, '').slice(0, 160)
          : prev.seoMetaDescription,
        sku: data.sku || prev.sku,
      }));

      if (validImages.length > 0) {
        setImagePreview(validImages[0]);
      }

      setFieldErrors((prev) => {
        const next = { ...prev };
        if (parsedTitle) delete next.title;
        if (parsedBasePrice > 0) delete next.basePrice;
        if (matchedCategoryIds.length > 0) delete next.categories;
        return next;
      });

      const priceMsg = parsedBasePrice > 0 ? ` • ${parsedBasePrice} TL` : '';
      const imgMsg = validImages.length > 0 ? ` • ${validImages.length} Görsel` : '';
      const tagMsg = autoTags.length > 0 ? ` • ${autoTags.length} Etiket` : '';
      const matchedCatNames = categories.filter((c) => matchedCategoryIds.includes(c.id)).map((c) => c.name);
      const catMsg = matchedCatNames.length > 0 ? ` • ${matchedCatNames.slice(0, 2).join(', ')}` : '';

      toast.success(`"${parsedTitle || 'Ürün'}"${priceMsg}${imgMsg}${tagMsg}${catMsg} başarıyla aktarıldı!`, {
        duration: 6000,
        icon: '🎉',
      });
    } catch (error: any) {
      console.error('Import hatası:', error);
      const errMsg = error.message || 'Ürün bilgileri çekilemedi.';
      toast.error(errMsg, { duration: 6000 });
      if (errMsg.includes('403') || errMsg.includes('koruma') || errMsg.includes('erişilemedi')) {
        setImportMode('html');
      }
    } finally {
      setIsImporting(false);
    }
  };

  const isInstagramUrlValid = (value: string) => {
    if (!value.trim()) {
      return true;
    }

    return /^https?:\/\/(www\.)?(instagram\.com|instagr\.am)\//i.test(value.trim());
  };

  // AI SEO generation handler
  const handleAiGenerate = async (type: 'seoTitle' | 'seoDescription' | 'description') => {
    if (!formData.title.trim()) {
      toast.error('Önce ürün başlığını girin');
      return;
    }
    setAiLoading(type);
    try {
      const result = await aiApi.generateSeo({
        title: formData.title,
        description: formData.description,
        type,
      });
      if (type === 'seoTitle') {
        setFormData((prev) => ({ ...prev, seoTitle: result.slice(0, 60) }));
      } else if (type === 'seoDescription') {
        setFormData((prev) => ({ ...prev, seoMetaDescription: result.slice(0, 160) }));
      } else if (type === 'description') {
        setFormData((prev) => ({ ...prev, description: result }));
      }
      toast.success('AI içerik oluşturuldu');
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'AI içerik oluşturulamadı');
    } finally {
      setAiLoading(null);
    }
  };

  const handleGenerateProductCopy = async () => {
    if (!formData.title.trim()) {
      toast.error('Önce ürün başlığını girin');
      return;
    }

    const previewUrl = imagePreview || (formData.images && formData.images.length > 0 ? formData.images[0] : '');
    if (!previewUrl) {
      toast.error('Önce bir ürün görseli ekleyin veya seçin');
      return;
    }

    setAiLoading('product-copy');
    try {
      console.log('🤖 AI SEO üretimi başlatılıyor...', {
        title: formData.title,
        descriptionLength: formData.description?.length || 0,
        imageUrl: previewUrl,
      });

      const result = await aiApi.generateProductCopy({
        title: formData.title,
        description: formData.description,
        imageUrl: previewUrl,
        tags: formData.tags,
      });

      setFormData((prev) => ({
        ...prev,
        seoTitle: result.seoTitle.slice(0, 60),
        seoMetaDescription: result.seoDescription.slice(0, 160),
        description: result.description,
      }));
      toast.success('SEO başlığı, meta açıklama ve ürün açıklaması oluşturuldu');
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'AI içerik oluşturulamadı');
    } finally {
      setAiLoading(null);
    }
  };

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end (image reorder)
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = formData.images.findIndex((img) => img === active.id);
      const newIndex = formData.images.findIndex((img) => img === over.id);

      const newImages = arrayMove(formData.images, oldIndex, newIndex);
      
      setFormData((prev) => ({
        ...prev,
        images: newImages,
      }));

      // Önizleme resmi değişmediyse aynı resmi tut
      if (imagePreview && newImages.includes(imagePreview)) {
        setImagePreview(imagePreview);
      } else if (newImages.length > 0) {
        setImagePreview(newImages[0]);
      }

      toast.success('Resim sırası değiştirildi');
    }
  };

  // Kategorileri yükle
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await categoryApi.getCategories();
        setCategories(data);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, []);

  // Edit mode: ürünü yükle
  useEffect(() => {
    if (isEditMode && productId) {
      const fetchProduct = async () => {
        try {
          setLoading(true);
          const product = await productApi.getProduct(productId);
          console.log('Yüklenen ürün:', product);
          console.log('Ürün resimleri:', product.images);
          console.log('Resim sayısı:', product.images?.length || 0);
          
          setFormData({
            title: product.title,
            slug: product.slug,
            description: product.description,
            categories: product.categories,
            tags: product.tags,
            images: product.images,
            videoUrl: product.videoUrl || '',
            videoPosterUrl: product.videoPosterUrl || '',
            videoAspectRatio: product.videoAspectRatio || '9:16',
            instagramVideoUrl: product.instagramVideoUrl || '',
            basePrice: product.basePrice,
            campaignPrice: product.campaignPrice,
            discountPercentage: product.discountPercentage,
            costPrice: product.costPrice,
            sku: product.sku,
            stock: product.stock,
            trackInventory: product.trackInventory,
            isAvailable: product.isAvailable,
            isShippable: product.isShippable === true,
            isDirectSale: product.isDirectSale === true || product.isShippable === true,
            estimatedShippingDays: product.estimatedShippingDays ?? undefined,
            isSameDayDelivery: product.isSameDayDelivery,
            deliveryFee: product.deliveryFee,
            seoTitle: product.seoTitle,
            seoMetaDescription: product.seoMetaDescription,
            status: product.status,
            featured: product.featured === true,
            featuredOrder: product.featuredOrder ?? undefined,
            vendorIds: product.vendorIds,
            extras: product.extras,
          });
          if (product.images[0]) {
            setImagePreview(product.images[0]);
          }
        } catch (error) {
          toast.error('Ürün yüklenmedi');
          navigate('/admin/products');
        } finally {
          setLoading(false);
        }
      };
      fetchProduct();
    }
  }, [isEditMode, productId, navigate]);

  // Title değiştiğinde slug auto-generate
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const title = e.target.value;
    setFormData((prev) => ({
      ...prev,
      title,
      slug: generateSlug(title),
    }));
  };

  // Fiyat değiştiğinde discount auto-calculate
  const handlePriceChange = (field: 'basePrice' | 'campaignPrice' | 'costPrice', value: string) => {
    const numValue = value === '' ? 0 : parseFloat(value) || 0;
    setFormData((prev) => {
      const updatedForm = { ...prev, [field]: numValue };
      const discount = calculateDiscountPercentage(updatedForm.basePrice, updatedForm.campaignPrice);
      return {
        ...updatedForm,
        discountPercentage: discount,
      };
    });

    // Real-time validation
    const errors = {...fieldErrors};
    
    if (field === 'basePrice') {
      if (value === '') {
        // Empty, let user continue typing
        delete errors.basePrice;
      } else if (numValue <= 0) {
        errors.basePrice = 'Satış fiyatı 0 dan büyük olmalıdır';
      } else {
        delete errors.basePrice;
      }
    }

    if (field === 'campaignPrice') {
      if (value === '' || numValue === 0) {
        delete errors.campaignPrice;
      } else if (numValue < 0) {
        errors.campaignPrice = 'Kampanya fiyatı negatif olamaz';
      } else if (numValue >= formData.basePrice && formData.basePrice > 0) {
        errors.campaignPrice = 'Kampanya fiyatı satış fiyatından düşük olmalıdır';
      } else {
        delete errors.campaignPrice;
      }
    }

    if (field === 'costPrice') {
      if (numValue < 0) {
        errors.costPrice = 'Maliyet fiyatı negatif olamaz';
      } else {
        delete errors.costPrice;
      }
    }

    setFieldErrors(errors);
  };

  // Kategori select
  const handleCategoryToggle = (categoryId: string) => {
    setFormData((prev) => ({
      ...prev,
      categories: prev.categories.includes(categoryId)
        ? prev.categories.filter(id => id !== categoryId)
        : [...prev.categories, categoryId],
    }));
  };

  // Tag ekleme
  const handleAddTag = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      setFormData((prev) => {
        if (prev.tags.includes(tagInput.trim())) return prev;
        return {
          ...prev,
          tags: [...prev.tags, tagInput.trim()],
        };
      });
      setTagInput('');
    }
  };

  // Tag silme
  const handleRemoveTag = (tag: string) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag),
    }));
  };

  const handleVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setSubmitting(true);
      const downloadURL = await uploadFileToStorageV2(file, 'products/videos', 'product-video');
      setFormData((prev) => ({
        ...prev,
        videoUrl: downloadURL,
      }));
      toast.success('Ürün videosu yüklendi');
    } catch (error) {
      console.error('Video upload error:', error);
      toast.error('Video yüklenemedi');
    } finally {
      setSubmitting(false);
      e.target.value = '';
    }
  };

  const handleRemoveVideo = async () => {
    const { videoUrl } = formData;
    setFormData((prev) => ({
      ...prev,
      videoUrl: '',
      videoPosterUrl: '',
    }));

    if (!videoUrl) {
      return;
    }

    try {
      await deleteImageFromStorage(videoUrl);
      toast.success('Ürün videosu silindi');
    } catch (error) {
      console.warn('Video deletion failed:', error);
      toast.error('Video formdan kaldırıldı ama Storage tarafında silinemedi');
    }
  };

  // Resim yükleme
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    try {
      setSubmitting(true);
      const uploadedUrls: string[] = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        // Firebase Storage'a yükle
        const downloadURL = await uploadImageToStorageV2(file, 'products', 'product');
        uploadedUrls.push(downloadURL);
        console.log(`Resim ${i + 1} yüklendi:`, downloadURL);
        
        // İlk resmi preview olarak göster
        if (formData.images.length === 0 && i === 0) {
          setImagePreview(downloadURL);
        }
      }

      console.log('Tüm yüklenen resimler:', uploadedUrls);
      console.log('Önceki resimler:', formData.images);

      setFormData((prev) => {
        const newImages = [...prev.images, ...uploadedUrls];
        console.log('Yeni resim listesi:', newImages);
        return {
          ...prev,
          images: newImages,
        };
      });

      toast.success(`${uploadedUrls.length} görsel yüklendi`);
    } catch (error) {
      console.error('Image upload error:', error);
      toast.error('Resim yüklenemedi');
    } finally {
      setSubmitting(false);
      // Input temizle
      e.target.value = '';
    }
  };

  // Resim sil
  const handleRemoveImage = async (index: number) => {
    const imageUrl = formData.images[index];
    setFormData((prev) => {
      const newImages = prev.images.filter((_, i) => i !== index);
      if (imagePreview === imageUrl) {
        setImagePreview(newImages.length > 0 ? newImages[0] : '');
      }
      return {
        ...prev,
        images: newImages,
      };
    });

    // Storage'dan sil (background'da)
    try {
      if (imageUrl && imageUrl.includes('firebasestorage.googleapis.com')) {
        await deleteImageFromStorage(imageUrl);
      }
    } catch (error) {
      console.warn('Storage image delete error:', error);
    }
  };

  // Form gönder
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Toplam validation kontrol et
    const validation = validateProductForm(formData);
    
    // Hataları fieldErrors'a set et
    const newErrors: Record<string, string> = {};
    if (!formData.title?.trim()) newErrors.title = 'Ürün adı zorunludur';
    if (!formData.slug?.trim()) newErrors.slug = 'Slug zorunludur';
    if (!formData.description?.trim()) newErrors.description = 'Ürün detayları zorunludur';
    if (formData.categories?.length === 0) newErrors.categories = 'En az bir kategori seçilmelidir';
    if (formData.basePrice <= 0) newErrors.basePrice = 'Satış fiyatı 0 dan büyük olmalıdır';
    if (formData.campaignPrice < 0) newErrors.campaignPrice = 'Kampanya fiyatı negatif olamaz';
    if (formData.images?.length === 0) newErrors.images = 'En az bir resim eklemen gerelidir';
    if (formData.isShippable && (!formData.estimatedShippingDays || formData.estimatedShippingDays < 1)) {
      newErrors.estimatedShippingDays = 'Tahmini teslim suresi en az 1 gun olmalidir';
    }
    if (formData.featured && formData.featuredOrder !== undefined && formData.featuredOrder !== null && formData.featuredOrder < 1) {
      newErrors.featuredOrder = 'Öne çıkan sırası 1 veya daha büyük olmalıdır';
    }
    if (formData.instagramVideoUrl && !isInstagramUrlValid(formData.instagramVideoUrl)) {
      newErrors.instagramVideoUrl = 'Geçerli bir Instagram video URL girin';
    }
    
    setFieldErrors(newErrors);

    if (!validation.valid) {
      validation.errors.forEach(error => toast.error(error));
      return;
    }

    try {
      setSubmitting(true);
      if (isEditMode && productId) {
        await productApi.updateProduct(productId, formData);
        toast.success('Ürün güncellendi');
      } else {
        await productApi.createProduct(formData);
        toast.success('Ürün oluşturuldu');
      }
      navigate('/admin/products');
    } catch (error) {
      toast.error(isEditMode ? 'Ürün güncellenemedi' : 'Ürün oluşturulamadı');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white/90 dark:bg-slate-900/90 backdrop-blur-md border-b border-slate-200/80 dark:border-slate-800/80 shadow-xs">
        <div className="max-w-[1680px] mx-auto px-3 sm:px-4 xl:px-6 2xl:px-8 py-3.5 flex items-center justify-between">
          <h1 className="text-xl sm:text-2xl font-extrabold text-slate-900 dark:text-slate-100 tracking-tight">
            {isEditMode ? 'Ürünü Düzenle' : 'Yeni Ürün Ekle'}
          </h1>
          <div className="flex gap-2.5">
            <button
              onClick={() => navigate('/admin/products')}
              className="btn btn-secondary"
            >
              İptal
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="btn btn-primary"
            >
              {submitting ? 'Kaydediliyor...' : 'Kaydet'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1680px] mx-auto px-3 sm:px-4 xl:px-6 2xl:px-8 py-6 xl:py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-12 2xl:gap-6">
          <div className="space-y-6 xl:col-span-8">

          {/* RAKİP / DIŞ SİTEDEN URL İLE İÇE AKTARMA */}
          {!isEditMode && (
            <div className="relative overflow-hidden rounded-2xl border border-indigo-200/90 dark:border-indigo-900/60 bg-gradient-to-br from-indigo-50/90 via-purple-50/50 to-pink-50/30 dark:from-slate-900 dark:via-indigo-950/40 dark:to-purple-950/20 p-5 sm:p-6 shadow-sm">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                <div className="flex items-start gap-3.5">
                  <div className="w-10 h-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center shadow-md shadow-indigo-500/20 flex-shrink-0 mt-0.5">
                    <Globe className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-base sm:text-lg font-bold text-slate-900 dark:text-slate-100">
                        Rakip / Dış Siteden Hızlı Ürün Çek
                      </h2>
                      <span className="px-2 py-0.5 text-[11px] font-semibold bg-indigo-100 text-indigo-700 dark:bg-indigo-900/60 dark:text-indigo-300 rounded-full">
                        Akıllı İçe Aktarma
                      </span>
                    </div>
                    <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 mt-0.5">
                      İstediğiniz çiçek veya e-ticaret sitesinin ürün linkini girin; başlık, açıklama, görseller ve fiyat otomatik çekilsin.
                    </p>
                  </div>
                </div>

                {/* Mod Seçimi */}
                <div className="flex items-center bg-white/80 dark:bg-slate-800/80 p-1 rounded-xl border border-slate-200 dark:border-slate-700 text-xs font-medium self-start md:self-auto">
                  <button
                    type="button"
                    onClick={() => setImportMode('url')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all ${
                      importMode === 'url'
                        ? 'bg-indigo-600 text-white shadow-sm font-semibold'
                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
                    }`}
                  >
                    <Link2 className="w-3.5 h-3.5" />
                    <span>Link ile Çek</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setImportMode('html')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all ${
                      importMode === 'html'
                        ? 'bg-indigo-600 text-white shadow-sm font-semibold'
                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
                    }`}
                  >
                    <FileCode className="w-3.5 h-3.5" />
                    <span>Sayfa Kaynağı (HTML)</span>
                  </button>
                </div>
              </div>

              {importMode === 'url' ? (
                <div className="flex flex-col sm:flex-row gap-2.5">
                  <div className="relative flex-1">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                      <Link2 className="w-4 h-4" />
                    </div>
                    <input
                      type="url"
                      value={importUrl}
                      onChange={(e) => setImportUrl(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleImportFromUrl();
                        }
                      }}
                      placeholder="Örn: https://www.ciceksepeti.com/... veya herhangi bir ürün linki"
                      disabled={isImporting}
                      className="w-full pl-10 pr-4 py-3 text-sm rounded-xl border border-indigo-200 dark:border-slate-700 bg-white/90 dark:bg-slate-900/90 text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm transition-all"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => handleImportFromUrl()}
                    disabled={isImporting || !importUrl.trim()}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed rounded-xl shadow-md shadow-indigo-500/20 transition-all cursor-pointer whitespace-nowrap"
                  >
                    {isImporting ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        <span>Veriler Çekiliyor...</span>
                      </>
                    ) : (
                      <>
                        <DownloadCloud className="w-4 h-4" />
                        <span>Ürün Bilgilerini Getir</span>
                      </>
                    )}
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                      <Link2 className="w-4 h-4" />
                    </div>
                    <input
                      type="url"
                      value={importUrl}
                      onChange={(e) => setImportUrl(e.target.value)}
                      placeholder="Hedef Ürün URL'si (Görsel adreslerini tamamlamak için)"
                      disabled={isImporting}
                      className="w-full pl-10 pr-4 py-2.5 text-sm rounded-xl border border-indigo-200 dark:border-slate-700 bg-white/90 dark:bg-slate-900/90 text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm transition-all"
                    />
                  </div>
                  <div>
                    <textarea
                      value={importHtml}
                      onChange={(e) => setImportHtml(e.target.value)}
                      placeholder="Sayfa kaynağını buraya yapıştırın (Sayfadayken sağ tıklayın -> 'Sayfa Kaynağını Görüntüle' -> Tümünü kopyalayıp buraya yapıştırın)..."
                      rows={4}
                      disabled={isImporting}
                      className="w-full p-3 font-mono text-xs rounded-xl border border-indigo-200 dark:border-slate-700 bg-white/90 dark:bg-slate-900/90 text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm transition-all resize-y"
                    />
                  </div>
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => handleImportFromUrl()}
                      disabled={isImporting || !importUrl.trim() || !importHtml.trim()}
                      className="inline-flex items-center justify-center gap-2 px-6 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed rounded-xl shadow-md shadow-indigo-500/20 transition-all cursor-pointer whitespace-nowrap"
                    >
                      {isImporting ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          <span>Ayrıştırılıyor & Görseller Yükleniyor...</span>
                        </>
                      ) : (
                        <>
                          <Code2 className="w-4 h-4" />
                          <span>HTML'den Ürünü Çıkar ve Yükle</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-500 dark:text-slate-400">
                <span className="inline-flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> Başlık & Açıklama
                </span>
                <span className="inline-flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> Otomatik Fiyat (Düzenlenebilir)
                </span>
                <span className="inline-flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> Görseller Storage'a İndirilir
                </span>
                <span className="inline-flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> SEO ve Slug Hazırlanır
                </span>
              </div>
            </div>
          )}

          {/* TEMEL BİLGİLER */}
          <div className="card space-y-6">
            <div className="border-b border-slate-100 dark:border-slate-800 pb-4">
              <h2 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-slate-100">Temel Bilgiler</h2>
              <p className="mt-1 text-xs sm:text-sm text-slate-500 dark:text-slate-400">Başlık, kategori, görsel ve yayın durumu bilgilerini bu alandan yönetin.</p>
            </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-[minmax(0,2fr)_minmax(280px,1fr)]">
                {/* Başlık */}
                <div className="md:col-span-2 xl:col-span-1">
                  <label className="block text-xs sm:text-sm font-bold text-slate-700 dark:text-slate-300 mb-1.5">Ürün Adı *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={handleTitleChange}
                    placeholder="Örn: Kırmızı Gül Buketi"
                    className={`w-full px-4 py-2.5 text-sm rounded-xl border bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 ${
                      fieldErrors.title
                        ? 'border-rose-500 focus:ring-rose-500/30'
                        : 'border-slate-300 dark:border-slate-700 focus:ring-indigo-500/30 focus:border-indigo-500'
                    }`}
                    required
                  />
                  {fieldErrors.title && (
                    <p className="text-rose-500 text-xs mt-1 font-medium flex items-center gap-1">
                      <span>⚠</span> {fieldErrors.title}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-1">
                  {/* Slug */}
                  <div>
                    <label className="block text-xs sm:text-sm font-bold text-slate-700 dark:text-slate-300 mb-1.5">Slug (URL)</label>
                    <input
                      type="text"
                      value={formData.slug}
                      onChange={(e) => setFormData((prev) => ({ ...prev, slug: e.target.value }))}
                      placeholder="kirmizi-gul-buketi"
                      className="w-full px-4 py-2.5 text-xs font-mono rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500"
                    />
                    <p className="text-[11px] text-slate-400 mt-1">Otomatik oluşturulur</p>
                  </div>

                  {/* Durum */}
                  <div>
                    <label className="block text-xs sm:text-sm font-bold text-slate-700 dark:text-slate-300 mb-1.5">Yayın Durumu</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData((prev) => ({ ...prev, status: e.target.value as any }))}
                      className="w-full px-4 py-2.5 text-sm rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500"
                    >
                      <option value="active">✅ Aktif (Satışta)</option>
                      <option value="inactive">❌ Pasif (Gizli)</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Resim */}
              <div>
                <label className="block text-xs sm:text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">Ürün Görselleri *</label>
                
                {/* Upload Area */}
                <div className="mb-6 p-6 border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-2xl text-center cursor-pointer hover:border-indigo-500 transition-colors bg-slate-50/50 dark:bg-slate-800/30">
                  <label className="cursor-pointer block">
                    <Upload size={32} className="mx-auto mb-2 text-gray-400" />
                    <span className="text-sm font-medium text-gray-700">Resimleri Sürükle veya Tıkla</span>
                    <p className="text-xs text-gray-500 mt-1">Birden fazla resim seçebilirsin</p>
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                  </label>
                </div>

                {/* Validation Error */}
                {formData.images.length === 0 && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                    <span className="text-red-600 font-semibold text-sm">⚠ En az bir resim eklemen gerelidir</span>
                  </div>
                )}

                {/* Image Gallery */}
                {formData.images.length > 0 && (
                  <div>
                    <p className="text-sm text-gray-600 mb-3">
                      {formData.images.length} resim yüklendi. Resimleri sürükleyerek sıralamayı değiştirebilirsin.
                    </p>
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={formData.images}
                        strategy={rectSortingStrategy}
                      >
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                          {formData.images.map((image, index) => (
                            <SortableImageItem
                              key={image}
                              id={image}
                              image={image}
                              index={index}
                              isPreview={imagePreview === image}
                              onRemove={handleRemoveImage}
                              onSetPreview={setImagePreview}
                              onOpenPreview={setLightboxImage}
                            />
                          ))}
                        </div>
                      </SortableContext>
                    </DndContext>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <div>
                  <label className="block text-sm font-semibold mb-4 text-gray-700">Ürün Videosu</label>
                  <div className="p-5 border-2 border-dashed border-gray-300 rounded-lg text-center hover:border-blue-500 transition">
                    <label className="cursor-pointer block">
                      <Upload size={28} className="mx-auto mb-2 text-gray-400" />
                      <span className="text-sm font-medium text-gray-700">Dikey ürün videosu yükle</span>
                      <p className="text-xs text-gray-500 mt-1">Tercihen 9:16 MP4 veya MOV. Reel tarzı videolar için uygundur.</p>
                      <input
                        type="file"
                        accept="video/mp4,video/quicktime,video/webm"
                        onChange={handleVideoUpload}
                        className="hidden"
                      />
                    </label>
                  </div>

                  {formData.videoUrl && (
                    <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-4">
                      <video
                        src={formData.videoUrl}
                        controls
                        className="w-full max-w-xs rounded-xl bg-black"
                        style={{ aspectRatio: (formData.videoAspectRatio || '9:16').replace(':', ' / ') }}
                      />
                      <div className="mt-3 flex items-center gap-3">
                        <button
                          type="button"
                          onClick={handleRemoveVideo}
                          className="px-3 py-2 text-xs font-medium rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition"
                        >
                          Videoyu Kaldır
                        </button>
                        <select
                          value={formData.videoAspectRatio || '9:16'}
                          onChange={(e) => setFormData((prev) => ({ ...prev, videoAspectRatio: e.target.value as ProductFormData['videoAspectRatio'] }))}
                          className="px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="9:16">Dikey 9:16</option>
                          <option value="1:1">Kare 1:1</option>
                          <option value="16:9">Yatay 16:9</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">Instagram Video URL</label>
                  <input
                    type="url"
                    value={formData.instagramVideoUrl || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData((prev) => ({ ...prev, instagramVideoUrl: value }));

                      setFieldErrors((prev) => {
                        const next = { ...prev };
                        if (!value.trim() || isInstagramUrlValid(value)) {
                          delete next.instagramVideoUrl;
                        } else {
                          next.instagramVideoUrl = 'Geçerli bir Instagram video URL girin';
                        }
                        return next;
                      });
                    }}
                    placeholder="https://www.instagram.com/reel/..."
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                      fieldErrors.instagramVideoUrl
                        ? 'border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:ring-blue-500'
                    }`}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Ürün sayfasında kullanıcıya sosyal medya videosu olarak gösterilecek bağlantı.
                  </p>
                  {fieldErrors.instagramVideoUrl && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <span>⚠</span> {fieldErrors.instagramVideoUrl}
                    </p>
                  )}
                </div>
              </div>

              {/* Kategoriler */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-semibold text-gray-700">Kategoriler *</label>
                  <button
                    type="button"
                    onClick={handleAutoMatchCategories}
                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200 transition cursor-pointer"
                  >
                    <Sparkles size={13} className="text-indigo-600" />
                    <span>Otomatik Kategori Eşle</span>
                  </button>
                </div>
                <div className="border border-gray-300 rounded-lg overflow-y-auto" style={{ maxHeight: '300px', padding: '12px' }}>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {(() => {
                      const { mainCategories, subCategories } = organizeCategories(categories);
                      return mainCategories.map(mainCat => (
                        <div key={mainCat.id} className="border-r pr-4">
                          {/* Ana Kategori */}
                          <label className="flex items-center gap-2 cursor-pointer mb-3 p-1 rounded hover:bg-gray-50 transition">
                            <input
                              type="checkbox"
                              checked={formData.categories.includes(mainCat.id)}
                              onChange={() => handleCategoryToggle(mainCat.id)}
                              className="w-4 h-4 rounded"
                            />
                            <span className="text-xs font-bold text-gray-900">{mainCat.name}</span>
                          </label>

                          {/* Alt Kategoriler */}
                          {subCategories(mainCat.id).length > 0 && (
                            <div className="space-y-1 ml-2">
                              {subCategories(mainCat.id).map(subCat => (
                                <label key={subCat.id} className="flex items-center gap-2 cursor-pointer p-1 rounded hover:bg-gray-50 transition">
                                  <input
                                    type="checkbox"
                                    checked={formData.categories.includes(subCat.id)}
                                    onChange={() => handleCategoryToggle(subCat.id)}
                                    className="w-3 h-3 rounded"
                                  />
                                  <span className="text-xs text-gray-600">└</span>
                                  <span className="text-xs text-gray-700">{subCat.name}</span>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      ));
                    })()}
                  </div>
                </div>
                {fieldErrors.categories && (
                  <p className="text-red-500 text-xs flex items-center gap-1 mt-2">
                    <span>⚠</span> {fieldErrors.categories}
                  </p>
                )}
              </div>
          </div>

          {/* SEO */}
          <div className="bg-white rounded-lg p-6 space-y-6">
            <div className="border-b border-gray-200 pb-4">
              <h2 className="text-xl font-semibold text-gray-900">SEO ve Icerik</h2>
              <p className="mt-1 text-sm text-gray-500">Aciklama, etiket ve SEO alanlari tek akista duzenlenebilir.</p>
            </div>
              {/* Açıklama */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-semibold text-gray-700">Ürün Açıklaması</label>
                  <button
                    type="button"
                    onClick={handleGenerateProductCopy}
                    disabled={aiLoading !== null}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-gradient-to-r from-purple-500 to-indigo-500 text-white hover:from-purple-600 hover:to-indigo-600 disabled:opacity-50 transition shadow-sm"
                  >
                    <Sparkles size={14} className={aiLoading === 'product-copy' ? 'animate-spin' : ''} />
                    {aiLoading === 'product-copy' ? 'Görsel Analiz Ediliyor...' : 'Görsel ile SEO Uyumlu Doldur'}
                  </button>
                </div>
                <div className="border border-gray-300 rounded-lg overflow-hidden" style={{ height: '400px' }}>
                  <ReactQuill
                    value={formData.description}
                    onChange={(value) => setFormData((prev) => ({ ...prev, description: value }))}
                    theme="snow"
                    placeholder="Ürün açıklaması yazın..."
                    style={{ height: '100%' }}
                    modules={{
                      toolbar: [
                        [{ 'header': [1, 2, 3, false] }],
                        ['bold', 'italic', 'underline', 'strike'],
                        ['blockquote', 'code-block'],
                        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                        [{ 'script': 'sub'}, { 'script': 'super' }],
                        [{ 'indent': '-1'}, { 'indent': '+1' }],
                        [{ 'size': ['small', false, 'large', 'huge'] }],
                        [{ 'color': [] }, { 'background': [] }],
                        [{ 'align': [] }],
                        ['link', 'image', 'video'],
                        ['clean']
                      ]
                    }}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                {/* Etiketler */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-semibold text-gray-700">Etiketler</label>
                    <button
                      type="button"
                      onClick={handleAutoGenerateTags}
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200 transition cursor-pointer"
                      title="Başlık, açıklama ve kategorilerden otomatik etiket üretir"
                    >
                      <Sparkles size={13} className="text-indigo-600" />
                      <span>Otomatik Etiket Oluştur</span>
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {formData.tags.map(tag => (
                      <span key={tag} className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm flex items-center gap-2">
                        {tag}
                        <button
                          type="button"
                          onClick={() => handleRemoveTag(tag)}
                          className="hover:text-blue-900"
                        >
                          <X size={16} />
                        </button>
                      </span>
                    ))}
                  </div>
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={handleAddTag}
                    placeholder="Etiket girin ve Enter'e basın"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* AI Auto-generate all */}
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-4">
                  <div className="flex h-full flex-col justify-between gap-4">
                    <div>
                      <h4 className="text-sm font-bold text-purple-800 flex items-center gap-2">
                        <Sparkles size={16} /> AI Destekli SEO
                      </h4>
                      <p className="text-xs text-purple-600 mt-0.5">Seçili önizleme görseli ile SEO başlığı, meta açıklama ve ürün içeriğini birlikte üretin</p>
                    </div>
                    <div>
                      <button
                        type="button"
                        onClick={handleGenerateProductCopy}
                        disabled={aiLoading !== null}
                        className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-gradient-to-r from-purple-500 to-indigo-500 text-white hover:from-purple-600 hover:to-indigo-600 disabled:opacity-50 transition shadow-sm"
                      >
                        <Sparkles size={15} className={aiLoading === 'product-copy' ? 'animate-spin' : ''} />
                        {aiLoading === 'product-copy' ? 'Oluşturuluyor...' : 'Görsel ile Tümünü Doldur'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                <div>
                  <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <label className="block text-sm font-semibold text-gray-700">SEO Başlığı (Max 60 karakter)</label>
                    <button
                      type="button"
                      onClick={() => handleAiGenerate('seoTitle')}
                      disabled={aiLoading !== null}
                      className="inline-flex items-center gap-1 self-start px-2.5 py-1 text-xs font-medium rounded-md bg-purple-100 text-purple-700 hover:bg-purple-200 disabled:opacity-50 transition"
                    >
                      <Sparkles size={12} className={aiLoading === 'seoTitle' ? 'animate-spin' : ''} />
                      {aiLoading === 'seoTitle' ? 'Yazılıyor...' : 'AI ile Oluştur'}
                    </button>
                  </div>
                  <input
                    type="text"
                    value={formData.seoTitle}
                    onChange={(e) => {
                      const val = e.target.value.slice(0, 60);
                      setFormData((prev) => ({ ...prev, seoTitle: val }));
                      if (val.trim()) {
                        const errors = {...fieldErrors};
                        delete errors.seoTitle;
                        setFieldErrors(errors);
                      }
                    }}
                    maxLength={60}
                    placeholder="Arama motorları için başlık"
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                      fieldErrors.seoTitle
                        ? 'border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:ring-blue-500'
                    }`}
                  />
                  <div className="mt-1 flex items-center justify-between gap-3">
                    <p className="text-xs text-gray-500">{formData.seoTitle.length}/60</p>
                    {fieldErrors.seoTitle && (
                      <p className="text-red-500 text-xs flex items-center gap-1">
                        <span>⚠</span> {fieldErrors.seoTitle}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <label className="block text-sm font-semibold text-gray-700">SEO Meta Açıklaması (Max 160 karakter) - İsteğe Bağlı</label>
                    <button
                      type="button"
                      onClick={() => handleAiGenerate('seoDescription')}
                      disabled={aiLoading !== null}
                      className="inline-flex items-center gap-1 self-start px-2.5 py-1 text-xs font-medium rounded-md bg-purple-100 text-purple-700 hover:bg-purple-200 disabled:opacity-50 transition"
                    >
                      <Sparkles size={12} className={aiLoading === 'seoDescription' ? 'animate-spin' : ''} />
                      {aiLoading === 'seoDescription' ? 'Yazılıyor...' : 'AI ile Oluştur'}
                    </button>
                  </div>
                  <textarea
                    value={formData.seoMetaDescription}
                    onChange={(e) => {
                      const val = e.target.value.slice(0, 160);
                      setFormData((prev) => ({ ...prev, seoMetaDescription: val }));
                      if (val.trim()) {
                        const errors = {...fieldErrors};
                        delete errors.seoMetaDescription;
                        setFieldErrors(errors);
                      }
                    }}
                    maxLength={160}
                    placeholder="Arama sonuçlarında görünecek açıklama"
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                      fieldErrors.seoMetaDescription
                        ? 'border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:ring-blue-500'
                    }`}
                    rows={3}
                  />
                  <div className="mt-1 flex items-center justify-between gap-3">
                    <p className="text-xs text-gray-500">{formData.seoMetaDescription.length}/160</p>
                    {fieldErrors.seoMetaDescription && (
                      <p className="text-red-500 text-xs flex items-center gap-1">
                        <span>⚠</span> {fieldErrors.seoMetaDescription}
                      </p>
                    )}
                  </div>
                </div>
              </div>
          </div>
          </div>

          <div className="space-y-5 xl:col-span-4">
          {/* FİYATLANDIRMA */}
          <div className="bg-white rounded-lg p-6 space-y-6">
            <div className="border-b border-gray-200 pb-4">
              <h2 className="text-xl font-semibold text-gray-900">Fiyatlandirma</h2>
              <p className="mt-1 text-sm text-gray-500">Satis, kampanya ve maliyet alanlari burada birlikte gorunur.</p>
            </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">
                    Baz Fiyat (₺) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.basePrice === 0 ? '' : formData.basePrice}
                    onChange={(e) => handlePriceChange('basePrice', e.target.value)}
                    placeholder="0.00"
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                      fieldErrors.basePrice
                        ? 'border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:ring-blue-500'
                    }`}
                    required
                  />
                  {fieldErrors.basePrice && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <span>⚠</span> {fieldErrors.basePrice}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">
                    Kampanya Fiyatı (₺)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.campaignPrice === 0 ? '' : formData.campaignPrice}
                    onChange={(e) => handlePriceChange('campaignPrice', e.target.value)}
                    placeholder="0.00"
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                      fieldErrors.campaignPrice
                        ? 'border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:ring-blue-500'
                    }`}
                  />
                  {fieldErrors.campaignPrice && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <span>⚠</span> {fieldErrors.campaignPrice}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">İndirim %</label>
                  <input
                    type="number"
                    value={formData.discountPercentage}
                    readOnly
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
                  />
                  <p className="text-xs text-gray-500 mt-1">Otomatik hesaplanır</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">
                    Birim Kalem / Alış Maliyeti (₺)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.costPrice === 0 ? '' : formData.costPrice}
                    onChange={(e) => handlePriceChange('costPrice', e.target.value)}
                    placeholder="0.00"
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                      fieldErrors.costPrice
                        ? 'border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:ring-blue-500'
                    }`}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.isShippable 
                      ? 'Doğrudan satışta sipariş kâr-zarar hesabında düşülecek birim ürün maliyetiniz.' 
                      : 'Vendor kâr ortaklığı modelinde düşülecek hammadde maliyeti.'}
                  </p>
                  {fieldErrors.costPrice && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <span>⚠</span> {fieldErrors.costPrice}
                    </p>
                  )}
                </div>
              </div>
          </div>

          {/* STOK */}
          <div className="bg-white rounded-lg p-6 space-y-6">
            <div className="border-b border-gray-200 pb-4">
              <h2 className="text-xl font-semibold text-gray-900">Stok</h2>
              <p className="mt-1 text-sm text-gray-500">Stok takibi, satis uygunlugu ve SKU ayarlari tek blokta yer alir.</p>
            </div>
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <div className="rounded-xl border border-gray-200 bg-gray-50/70 p-4">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.trackInventory}
                      onChange={(e) => setFormData((prev) => ({ ...prev, trackInventory: e.target.checked }))}
                      className="w-5 h-5 rounded"
                    />
                    <span className="font-semibold text-gray-700">Stok Takibi Yap</span>
                  </label>
                </div>

                <div className="rounded-xl border border-gray-200 bg-gray-50/70 p-4">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.isAvailable}
                      onChange={(e) => setFormData((prev) => ({ ...prev, isAvailable: e.target.checked }))}
                      disabled={!formData.trackInventory}
                      className="w-5 h-5 rounded"
                    />
                    <span className="font-semibold text-gray-700">Satışa Açık</span>
                  </label>
                  {!formData.trackInventory && (
                    <p className="mt-2 text-xs text-gray-500">Stok takibi kapaliyken satis durumu manuel degistirilemez.</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {formData.trackInventory && (
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-700">Stok Miktarı</label>
                    <input
                      type="number"
                      value={formData.stock}
                      onChange={(e) => setFormData((prev) => ({ ...prev, stock: parseInt(e.target.value) || 0 }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">SKU (İsteğe Bağlı)</label>
                  <input
                    type="text"
                    value={formData.sku}
                    onChange={(e) => setFormData((prev) => ({ ...prev, sku: e.target.value }))}
                    placeholder="Ürün SKU"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">Rating</label>
                  <input
                    type="number"
                    min="0"
                    max="5"
                    step="0.1"
                    value={0}
                    disabled
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">Otomatik hesaplanır</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">Yorum Sayısı</label>
                  <input
                    type="number"
                    min="0"
                    value={0}
                    disabled
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">Otomatik hesaplanır</p>
                </div>
              </div>
          </div>

          {/* SATIŞ & TEDARİK MODELİ VE TESLİMAT */}
          <div className="bg-white rounded-2xl border border-gray-200/80 p-6 space-y-5 shadow-sm">
            <div className="border-b border-gray-100 pb-3.5">
              <div className="flex items-center gap-2">
                <span className="text-lg">📦</span>
                <h2 className="text-lg font-bold text-gray-900">Satış & Tedarik Modeli</h2>
              </div>
              <p className="mt-1 text-xs text-gray-500">Ürünün sahibini, teslimat tipini ve muhasebe modelini belirleyin.</p>
            </div>

            {/* Model Seçim Kartları (Dikey ve Ferah Liste) */}
            <div className="flex flex-col gap-3.5">
              {/* Option 1: Vendor Çiçekçi */}
              <div
                onClick={() => {
                  setFormData((prev) => ({
                    ...prev,
                    isShippable: false,
                    isDirectSale: false,
                    isSameDayDelivery: true,
                    estimatedShippingDays: undefined,
                  }));
                  setFieldErrors((prev) => {
                    const next = { ...prev };
                    delete next.estimatedShippingDays;
                    return next;
                  });
                }}
                className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  !formData.isShippable
                    ? 'border-blue-600 bg-blue-50/60 shadow-sm ring-1 ring-blue-500/20'
                    : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50/50'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="mt-0.5">
                    <input
                      type="radio"
                      name="sales_model"
                      checked={!formData.isShippable}
                      onChange={() => {}}
                      className="w-4 h-4 text-blue-600 focus:ring-blue-500 cursor-pointer"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-bold text-gray-900 flex items-center gap-1.5">
                        <span>🌸</span> Vendor / Çiçekçi Ürünü
                      </span>
                      {!formData.isShippable && (
                        <span className="text-[11px] font-bold bg-blue-600 text-white px-2 py-0.5 rounded-full flex-shrink-0">
                          Seçili
                        </span>
                      )}
                    </div>
                    <p className="mt-1.5 text-xs text-gray-600 leading-relaxed">
                      Yerel çiçekçinin ürünüdür. Kurye ile aynı gün teslim edilir. Ciro üzerinden komisyon kesilir, satıcıya hakediş aktarılır.
                    </p>
                  </div>
                </div>
              </div>

              {/* Option 2: TazeBiJest Doğrudan Satış (Kargo) */}
              <div
                onClick={() => {
                  setFormData((prev) => ({
                    ...prev,
                    isShippable: true,
                    isDirectSale: true,
                    isSameDayDelivery: false,
                    estimatedShippingDays: prev.estimatedShippingDays && prev.estimatedShippingDays > 0 ? prev.estimatedShippingDays : 3,
                  }));
                }}
                className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  formData.isShippable
                    ? 'border-emerald-600 bg-emerald-50/60 shadow-sm ring-1 ring-emerald-500/20'
                    : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50/50'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="mt-0.5">
                    <input
                      type="radio"
                      name="sales_model"
                      checked={formData.isShippable === true}
                      onChange={() => {}}
                      className="w-4 h-4 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-bold text-gray-900 flex items-center gap-1.5">
                        <span>📦</span> TazeBiJest Kendi Ürünümüz (Kargo)
                      </span>
                      {formData.isShippable && (
                        <span className="text-[11px] font-bold bg-emerald-600 text-white px-2 py-0.5 rounded-full flex-shrink-0">
                          Seçili
                        </span>
                      )}
                    </div>
                    <p className="mt-1.5 text-xs text-gray-600 leading-relaxed">
                      TazeBiJest'in kendi ürünüdür. Kargo ile gönderilir. Cironun %100'ü TazeBiJest'e aittir (Satıcı payı = 0 TL).
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Kargo Ayarları (Sadece kargo seçiliyse) */}
            {formData.isShippable && (
              <div className="p-4 rounded-xl border border-emerald-200 bg-emerald-50/50 space-y-3.5">
                <div className="flex items-center gap-1.5 text-xs font-bold text-emerald-900">
                  <span>🚚</span> Kargo Gönderim Ayarları
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold mb-1 text-gray-700">
                      Tahmini Kargo Teslim Süresi (Gün) *
                    </label>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={formData.estimatedShippingDays ?? ''}
                      onChange={(e) => {
                        const rawValue = e.target.value;
                        const nextValue = rawValue === '' ? undefined : parseInt(rawValue, 10) || 0;
                        setFormData((prev) => ({ ...prev, estimatedShippingDays: nextValue }));

                        setFieldErrors((prev) => {
                          const next = { ...prev };
                          if (rawValue === '' || (nextValue !== undefined && nextValue >= 1)) {
                            delete next.estimatedShippingDays;
                          } else {
                            next.estimatedShippingDays = 'Tahmini teslim süresi en az 1 gün olmalıdır';
                          }
                          return next;
                        });
                      }}
                      placeholder="3"
                      className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 bg-white ${
                        fieldErrors.estimatedShippingDays
                          ? 'border-red-500 focus:ring-red-500'
                          : 'border-gray-300 focus:ring-emerald-500'
                      }`}
                    />
                    <p className="text-[11px] text-gray-500 mt-1">Örn: 3 girilirse web/mobilde "3 iş gününde kapında" gösterilir.</p>
                    {fieldErrors.estimatedShippingDays && (
                      <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                        <span>⚠</span> {fieldErrors.estimatedShippingDays}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-semibold mb-1 text-gray-700">
                      Kargo / Teslimat Ücreti (₺)
                    </label>
                    <input
                      type="number"
                      value={formData.deliveryFee}
                      onChange={(e) => setFormData((prev) => ({ ...prev, deliveryFee: parseFloat(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white"
                    />
                    <p className="text-[11px] text-gray-500 mt-1">0 yazılırsa müşteriye "Ücretsiz Kargo" olarak sunulur.</p>
                  </div>
                </div>
              </div>
            )}

            {/* Yerel Kurye Ayarları (Sadece kurye seçiliyse) */}
            {!formData.isShippable && (
              <div className="space-y-3 pt-1">
                <div>
                  <label className="block text-xs font-semibold mb-1 text-gray-700">
                    Kurye Teslimat Ücreti (₺)
                  </label>
                  <input
                    type="number"
                    value={formData.deliveryFee}
                    onChange={(e) => setFormData((prev) => ({ ...prev, deliveryFee: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  />
                  <p className="text-[11px] text-gray-500 mt-1">0 yazılırsa ücretsiz teslimat</p>
                </div>
                <div className="pt-1">
                  <label className="flex items-center gap-2.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.isSameDayDelivery}
                      onChange={(e) => setFormData((prev) => ({ ...prev, isSameDayDelivery: e.target.checked }))}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="text-xs font-semibold text-gray-700">Aynı Gün Kurye Teslimatı Mevcut</span>
                  </label>
                </div>
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg p-6 space-y-6">
            <div className="border-b border-gray-200 pb-4">
              <h2 className="text-xl font-semibold text-gray-900">Anasayfa</h2>
              <p className="mt-1 text-sm text-gray-500">Urunu anasayfada one cikarma ayarlari bu bolumde.</p>
            </div>
              <div className="rounded-xl border border-rose-100 bg-rose-50/70 p-4">
                <h3 className="text-base font-semibold text-gray-900">Anasayfa Öne Çıkarma</h3>
                <p className="mt-1 text-sm text-gray-600">
                  İşaretlenen ürünler web ve mobil anasayfada üstte gösterilir. Küçük sıra numarası daha yüksek öncelik demektir.
                </p>
              </div>

              <div className="space-y-4">
                <div className="rounded-xl border border-gray-200 bg-gray-50/70 p-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.featured === true}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setFormData((prev) => ({
                          ...prev,
                          featured: checked,
                          featuredOrder: checked ? prev.featuredOrder : undefined,
                        }));

                        if (!checked && fieldErrors.featuredOrder) {
                          setFieldErrors((prev) => {
                            const errors = { ...prev };
                            delete errors.featuredOrder;
                            return errors;
                          });
                        }
                      }}
                      className="mt-0.5 w-5 h-5 rounded"
                    />
                    <span className="font-semibold leading-5 text-gray-700">Bu ürünü anasayfada öne çıkar</span>
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">Öne Çıkan Sırası</label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    disabled={formData.featured !== true}
                    value={getFeaturedOrderInputValue(formData.featuredOrder)}
                    onChange={(e) => {
                      const rawValue = e.target.value;
                      const nextValue = rawValue === '' ? undefined : parseInt(rawValue, 10) || 0;

                      setFormData((prev) => ({
                        ...prev,
                        featuredOrder: nextValue,
                      }));

                      setFieldErrors((prev) => {
                        const errors = { ...prev };
                        if (rawValue === '' || (nextValue !== undefined && nextValue >= 1)) {
                          delete errors.featuredOrder;
                        } else {
                          errors.featuredOrder = 'Öne çıkan sırası 1 veya daha büyük olmalıdır';
                        }
                        return errors;
                      });
                    }}
                    placeholder="1"
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 disabled:bg-gray-100 disabled:text-gray-400 ${
                      fieldErrors.featuredOrder
                        ? 'border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:ring-blue-500'
                    }`}
                  />
                  <div className="mt-1 space-y-1 text-xs text-gray-500">
                    <p>Sıra boş bırakılırsa ürün yine öne çıkar, ancak sıralı ürünlerin arkasında gösterilir.</p>
                    <p>Örnek: 1 en öncelikli ürün olur.</p>
                  </div>
                  {fieldErrors.featuredOrder && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <span>⚠</span> {fieldErrors.featuredOrder}
                    </p>
                  )}
                </div>
              </div>
            </div>

            </div>
            </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 flex justify-end gap-3">
            <button
              type="button"
              onClick={() => navigate('/admin/products')}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition"
            >
              {submitting ? 'Kaydediliyor...' : 'Kaydet'}
            </button>
          </div>
        </form>
        <ImagePreviewModal
          isOpen={!!lightboxImage}
          imageUrl={lightboxImage}
          title="Ürün görseli önizleme"
          onClose={() => setLightboxImage('')}
        />
      </div>
    </div>
  );
}
